import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Play, Pause, Loader, Save, Download, Trash2, AlertCircle, 
  CheckCircle, Volume2, RefreshCw, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';
import logger from '../utils/logger';
import Mixpanel from '../utils/mixpanel';

const MentalImageryModal = ({ onComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [savedMentalImagery, setSavedMentalImagery] = useState([]);
  const [currentMentalImagery, setCurrentMentalImagery] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  const audioRef = useRef(null);
  
  useEffect(() => {
    // Track page view
    Mixpanel.trackEnhancedPageView('/mental-imagery-modal', 'Mental Imagery Modal', {
      feature: 'Mental Imagery',
      action: 'Opened Modal'
    });
    
    // Check if we have an onboarding mental imagery ID
    const mentalImageryId = localStorage.getItem('onboardingMentalImageryId');
    
    if (mentalImageryId) {
      fetchMentalImageryById(mentalImageryId);
    }
    
    // Fetch all saved mental imagery sessions
    fetchSavedMentalImagery();
  }, []);

  // Fetch saved mental imagery sessions
  const fetchSavedMentalImagery = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/ai/mental-imagery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedMentalImagery(response.data);
    } catch (error) {
      logger.error('Error fetching saved mental imagery:', error);
      setError('Failed to fetch saved mental imagery sessions.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch a specific mental imagery by ID
  const fetchMentalImageryById = async (id) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/ai/mental-imagery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentMentalImagery(response.data);
      
      if (response.data.audioUrl && response.data.audioUrl !== "pending") {
        setAudioUrl(response.data.audioUrl);
      } else {
        setAudioUrl("pending");
      }
      
      // Track selection
      Mixpanel.trackFeatureAction('Mental Imagery', 'Selected Session', {
        sessionId: id
      });
    } catch (error) {
      logger.error('Error fetching mental imagery:', error);
      setError('Failed to fetch mental imagery session.');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a mental imagery session
  const deleteMentalImageryById = async (id) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      await axios.delete(`${API_URL}/api/ai/mental-imagery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If the deleted session was the current one, reset the current session
      if (currentMentalImagery && currentMentalImagery._id === id) {
        setCurrentMentalImagery(null);
        setAudioUrl('');
      }
      
      // Refresh the list of saved sessions
      await fetchSavedMentalImagery();
      
      // Track deletion
      Mixpanel.trackFeatureAction('Mental Imagery', 'Deleted Session', {
        sessionId: id
      });
      
      setMessage('Mental imagery session deleted successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      logger.error('Error deleting mental imagery:', error);
      setError('Failed to delete mental imagery session.');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const playAudio = () => {
    if (audioRef.current && audioUrl && audioUrl !== "pending") {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          
          // Track play event
          Mixpanel.trackFeatureAction('Mental Imagery', 'Played Audio', {
            sessionId: currentMentalImagery?._id
          });
        })
        .catch(error => {
          logger.error('Error playing audio:', error);
          setError('Failed to play audio. Please try again.');
        });
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    
    // Track completion
    Mixpanel.trackFeatureAction('Mental Imagery', 'Completed Audio', {
      sessionId: currentMentalImagery?._id
    });
  };

  const downloadAudio = () => {
    if (audioUrl && audioUrl !== "pending") {
      const a = document.createElement('a');
      a.href = audioUrl;
      a.download = `mental-imagery-${currentMentalImagery._id}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Track download
      Mixpanel.trackFeatureAction('Mental Imagery', 'Downloaded Audio', {
        sessionId: currentMentalImagery?._id
      });
    }
  };

  // Format time for display (MM:SS)
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Navigate to full Mental Imagery page
  const goToMentalImageryPage = () => {
    navigate('/mental-imagery');
    if (onComplete) onComplete();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">Mental Imagery</h1>
        <button 
          onClick={goToMentalImageryPage}
          className="btn btn-primary btn-sm"
        >
          Open Full Page
        </button>
      </div>
      
      {error && (
        <div className="alert alert-error mb-6">
          <AlertCircle className="h-6 w-6" />
          <span>{error}</span>
        </div>
      )}
      
      {message && (
        <div className="alert alert-success mb-6">
          <CheckCircle className="h-6 w-6" />
          <span>{message}</span>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">About Mental Imagery</h2>
        <p className="mb-4">
          Mental imagery is a powerful technique used by elite athletes to enhance performance. 
          By vividly imagining successful performance in your mind, you can improve muscle memory, 
          confidence, and execution during actual competition.
        </p>
        <p>
          Use this personalized mental imagery session regularly as part of your pre-performance routine 
          or during training to strengthen your mental game.
        </p>
      </div>
      
      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 flex justify-center items-center">
          <Loader className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-2">Loading your mental imagery session...</span>
        </div>
      ) : currentMentalImagery && audioUrl ? (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Your Mental Imagery Session</h2>
          
          <div className="bg-gray-100 p-4 rounded-lg mb-4">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center">
                <button
                  onClick={togglePlayPause}
                  className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full mr-3"
                  disabled={!audioUrl || audioUrl === "pending"}
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </button>
                <div>
                  <h3 className="font-medium">{currentMentalImagery.title}</h3>
                  <p className="text-sm text-gray-600">{currentMentalImagery.settings?.duration || 10} minutes</p>
                </div>
              </div>
            </div>
            
            {audioUrl && audioUrl !== "pending" ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">{formatTime(currentTime)}</span>
                  <span className="text-gray-600">{formatTime(duration)}</span>
                </div>
                <div className="w-full bg-gray-300 rounded-full h-1.5 mb-4">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  ></div>
                </div>
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleAudioEnded}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-yellow-700 flex items-center">
                  <AlertCircle size={18} className="mr-2" />
                  Audio is not available for this mental imagery session. You can still read the script below.
                </p>
              </div>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            {audioUrl && audioUrl !== "pending" && (
              <button
                onClick={downloadAudio}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center"
              >
                <Download className="mr-2" size={20} />
                Download Audio
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-center flex-col p-8">
            <Volume2 size={48} className="text-gray-400 mb-4" />
            <p className="text-gray-600 text-center">
              No mental imagery session is currently loaded. 
              {savedMentalImagery.length > 0 ? ' Select one from your library below.' : ' Complete the onboarding process to generate your personalized mental imagery.'}
            </p>
          </div>
        </div>
      )}
      
      {savedMentalImagery.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">My Mental Imagery Library</h2>
          
          <div className="space-y-4">
            {savedMentalImagery.map(imagery => (
              <div key={imagery._id} className="bg-gray-100 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center">
                    <button
                      onClick={() => {
                        setCurrentMentalImagery(imagery);
                        if (imagery.audioUrl && imagery.audioUrl !== "pending") {
                          setAudioUrl(imagery.audioUrl);
                          setIsPlaying(false);
                          setTimeout(() => {
                            playAudio();
                          }, 100);
                        } else {
                          setAudioUrl("");
                        }
                        
                        // Track selection
                        Mixpanel.trackFeatureAction('Mental Imagery', 'Selected Session', {
                          sessionId: imagery._id
                        });
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full mr-3"
                    >
                      <Play size={20} />
                    </button>
                    <div>
                      <h3 className="font-medium">{imagery.title}</h3>
                      <p className="text-sm text-gray-600">
                        {imagery.settings?.duration || 10} minutes • 
                        {new Date(imagery.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMentalImageryById(imagery._id);
                    }}
                    className="text-red-500 hover:text-red-700 p-2"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MentalImageryModal;
